"use client";
import React, { useState, useEffect, useMemo, useLayoutEffect, useRef } from "react";
import {
  Clock,
  User,
  CheckCircle,
  XCircle,
  Eye,
  X,
  ChevronDown,
  Star,
  Users,
  CalendarCheck,
  UserCheck,
  TrendingUp,
  ChevronsLeft,
  ChevronsRight,
  AlertCircle,
  MessageSquare,
  BarChart2,
  Calendar,
  Filter,
  Check,
} from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useLeaves } from "../../context/LeavesContext";
import { useLeaveTypes } from "../../context/LeaveTypeContext";
import { useAttendance } from "../../context/AttendanceContext";
import { useManagedashboard } from "../../context/ManagedashboardContext";
import { usePerformance } from "../../context/PerformanceContext";
import { useAuth } from "../../context/AuthContext";
import { useEmployee } from "../../context/EmployeeContext";
// ── Constants ────────────────────────────────────────────────────────────────
const TABS = [
  { key: "leave", label: "Leave Approval", icon: CalendarCheck },
  { key: "attendance", label: "Attendance Approval", icon: Clock },
  { key: "team", label: "Team Management", icon: Users },
  { key: "performance", label: "Performance Reviews", icon: TrendingUp },
];

const LEAVE_STATUS_OPTIONS = ["pending", "manager_approved", "approved", "rejected"];
const ATTENDANCE_STATUS_OPTIONS = ["present", "late", "half_day",];
const PERFORMANCE_STATUS_OPTIONS = ["not_started", "in_progress", "completed"];

const STATUS_LABEL = {
  pending: "Pending",
  manager_approved: "Manager Approved",
  approved: "Approved",
  rejected: "Rejected",
  not_started: "Not Started",
  in_progress: "In Progress",
  completed: "Completed",
  active: "Active",
  on_leave: "On Leave",
  remote: "Remote",
  present: "Present",
  late: "Late",
  half_day: "Half Day", 
  absent: "Absent",
};

const STATUS_CLASS = {
  pending: "bg-yellow-100 text-yellow-700",
  manager_approved: "bg-blue-100 text-blue-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  not_started: "bg-gray-100 text-gray-600",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  active: "bg-green-100 text-green-700",
  on_leave: "bg-yellow-100 text-yellow-700",
  remote: "bg-indigo-100 text-indigo-700",
  present: "bg-green-100 text-green-700",
  late: "bg-orange-100 text-orange-700",
  half_day: "bg-yellow-100 text-yellow-700",
  absent: "bg-red-100 text-red-700",
};

const STATUS_DOT = {
  pending: "bg-yellow-500",
  manager_approved: "bg-blue-500",
  approved: "bg-green-500",
  rejected: "bg-red-500",
  not_started: "bg-gray-400",
  in_progress: "bg-blue-500",
  completed: "bg-green-500",
  active: "bg-green-500",
  on_leave: "bg-yellow-500",
  remote: "bg-indigo-500",
  present: "bg-green-500",
  late: "bg-orange-500",
  half_day: "bg-yellow-500",
  absent: "bg-red-500",
};

const LEAVE_TYPE_CLASS = {
  sick: "bg-red-50 text-red-600 border border-red-200",
  casual: "bg-blue-50 text-blue-600 border border-blue-200",
  earned: "bg-sky-50 text-sky-600 border border-sky-200",
  maternity: "bg-pink-50 text-pink-600 border border-pink-200",
  paternity: "bg-indigo-50 text-indigo-600 border border-indigo-200",
  unpaid: "bg-gray-50 text-gray-600 border border-gray-200",
};

// ── Mock Data ─────────────────────────────────────────────────────────────────
const MOCK_LEAVES = [
  { id: 1, employee: "Priya Sharma", avatar: "PS", department: "Engineering", leaveType: "sick", from: "2024-02-05", to: "2024-02-07", days: 3, reason: "Fever and flu symptoms requiring rest and medical attention.", status: "pending", appliedOn: "2024-02-03", managerNote: "" },
  { id: 2, employee: "Rahul Mehta", avatar: "RM", department: "Sales", leaveType: "casual", from: "2024-02-10", to: "2024-02-10", days: 1, reason: "Personal work.", status: "approved", appliedOn: "2024-02-07", managerNote: "Approved. Enjoy your day off." },
  { id: 3, employee: "Anika Verma", avatar: "AV", department: "Design", leaveType: "earned", from: "2024-02-15", to: "2024-02-19", days: 5, reason: "Family vacation planned months in advance.", status: "pending", appliedOn: "2024-01-20", managerNote: "" },
  { id: 4, employee: "Karan Singh", avatar: "KS", department: "Marketing", leaveType: "sick", from: "2024-02-08", to: "2024-02-09", days: 2, reason: "Doctor's appointment and recovery.", status: "rejected", appliedOn: "2024-02-06", managerNote: "Insufficient leave balance." },
  { id: 5, employee: "Sneha Patel", avatar: "SP", department: "HR", leaveType: "maternity", from: "2024-03-01", to: "2024-05-31", days: 90, reason: "Maternity leave as per company policy.", status: "approved", appliedOn: "2024-01-15", managerNote: "Approved as per policy." },
  { id: 6, employee: "Dev Kumar", avatar: "DK", department: "Engineering", leaveType: "casual", from: "2024-02-22", to: "2024-02-22", days: 1, reason: "Home shifting.", status: "pending", appliedOn: "2024-02-18", managerNote: "" },
];

const MOCK_ATTENDANCE = [
  { id: 1, employee: "Priya Sharma", department: "Engineering", date: "2024-02-05", checkIn: "10:35 AM", checkOut: "07:15 PM", hours: "8h 40m", type: "late_arrival", reason: "Traffic delay due to road closure.", status: "pending" },
  { id: 2, employee: "Rahul Mehta", department: "Sales", date: "2024-02-06", checkIn: "—", checkOut: "—", hours: "0h", type: "absent", reason: "Medical emergency in family.", status: "pending" },
  { id: 3, employee: "Dev Kumar", department: "Engineering", date: "2024-02-04", checkIn: "09:00 AM", checkOut: "03:30 PM", hours: "6h 30m", type: "early_exit", reason: "Child's school event.", status: "approved" },
  { id: 4, employee: "Anika Verma", department: "Design", date: "2024-02-03", checkIn: "11:00 AM", checkOut: "08:00 PM", hours: "9h", type: "late_arrival", reason: "Metro breakdown.", status: "rejected" },
  { id: 5, employee: "Karan Singh", department: "Marketing", date: "2024-02-07", checkIn: "—", checkOut: "—", hours: "0h", type: "absent", reason: "Sick leave not applied in time.", status: "pending" },
];

const MOCK_TEAM = [
  { id: 1, name: "Priya Sharma", role: "Senior Engineer", department: "Engineering", email: "priya@company.com", joinDate: "2021-03-15", status: "active", leaveBalance: 12, attendance: 96, performanceScore: 4.5 },
  { id: 2, name: "Rahul Mehta", role: "Sales Executive", department: "Sales", email: "rahul@company.com", joinDate: "2022-07-01", status: "active", leaveBalance: 8, attendance: 91, performanceScore: 3.8 },
  { id: 3, name: "Anika Verma", role: "UI/UX Designer", department: "Design", email: "anika@company.com", joinDate: "2020-11-20", status: "on_leave", leaveBalance: 3, attendance: 88, performanceScore: 4.2 },
  { id: 4, name: "Karan Singh", role: "Marketing Analyst", department: "Marketing", email: "karan@company.com", joinDate: "2023-01-10", status: "active", leaveBalance: 14, attendance: 94, performanceScore: 3.5 },
  { id: 5, name: "Sneha Patel", role: "HR Specialist", department: "HR", email: "sneha@company.com", joinDate: "2019-06-05", status: "on_leave", leaveBalance: 0, attendance: 78, performanceScore: 4.0 },
  { id: 6, name: "Dev Kumar", role: "Backend Engineer", department: "Engineering", email: "dev@company.com", joinDate: "2022-04-18", status: "remote", leaveBalance: 10, attendance: 99, performanceScore: 4.7 },
];

const MOCK_PERFORMANCE = [
  { id: 1, employee: "Priya Sharma", department: "Engineering", reviewPeriod: "Q4 2023", reviewer: "Manager", score: 4.5, goals: 5, goalsCompleted: 5, status: "completed", dueDate: "2024-01-31", feedback: "Exceptional performance. Led 3 major projects to completion. Strong technical leadership." },
  { id: 2, employee: "Rahul Mehta", department: "Sales", reviewPeriod: "Q4 2023", reviewer: "Manager", score: 3.8, goals: 4, goalsCompleted: 3, status: "completed", dueDate: "2024-01-31", feedback: "Good performance. Achieved 95% of sales targets. Needs improvement in CRM documentation." },
  { id: 3, employee: "Anika Verma", department: "Design", reviewPeriod: "Q4 2023", reviewer: "Manager", score: null, goals: 4, goalsCompleted: 2, status: "in_progress", dueDate: "2024-02-15", feedback: "" },
  { id: 4, employee: "Karan Singh", department: "Marketing", reviewPeriod: "Q4 2023", reviewer: "Manager", score: null, goals: 3, goalsCompleted: 0, status: "not_started", dueDate: "2024-02-15", feedback: "" },
  { id: 5, employee: "Dev Kumar", department: "Engineering", reviewPeriod: "Q4 2023", reviewer: "Manager", score: 4.7, goals: 6, goalsCompleted: 6, status: "completed", dueDate: "2024-01-31", feedback: "Outstanding engineer. Zero production incidents. Mentored 2 junior devs." },
];

// ── Avatar helpers ────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  "bg-indigo-500", "bg-green-500", "bg-blue-500",
  "bg-pink-500", "bg-yellow-500", "bg-sky-500",
  "bg-red-500", "bg-teal-500",
];
const Avatar = ({ name, size = "sm" }) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const bg = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  const initials = name.trim().split(" ").filter(Boolean).map(p => p[0]?.toUpperCase()).slice(0, 2).join("");
  const sizeClass = size === "lg" ? "w-10 h-10 text-sm" : "w-7 h-7 text-xs";
  return (
    <div className="relative group inline-flex">
      <div className={`${sizeClass} flex items-center justify-center rounded-full text-white font-bold ${bg}`}>
        {initials}
      </div>
      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black text-white text-[11px] px-2 py-1 rounded z-50 whitespace-nowrap">
        {name}
      </div>
    </div>
  );
};

// ── Star Rating ───────────────────────────────────────────────────────────────
const StarRating = ({ score }) => {
  if (!score) return <span className="text-gray-400 text-xs">—</span>;
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={12} className={i <= Math.round(score) ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"} />
      ))}
      <span className="text-xs text-gray-700 ml-1">{score}</span>
    </div>
  );
};

// Blurs whatever element currently has focus. Call this proactively, on
// pointerdown/touchstart, BEFORE a page change is requested. Mobile browsers
// schedule a "scroll the focused element into view" correction whenever a
// focused control becomes disabled or blurs — and that correction can land
// well after our own scroll-lock restore has already finished, silently
// re-introducing the exact jump we're trying to prevent. Blurring early means
// the browser never has a focused element to "protect" by scrolling to it.
function blurActiveElement() {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
}

// Walks up from `node` and returns every scrollable ancestor (plus the page's
// own scrolling element). We don't assume `window` is what actually scrolls —
// some app shells put scrolling on an inner wrapper div instead — so we lock
// whichever element(s) can actually move.
function getScrollAncestors(node) {
  const result = [];
  let el = node ? node.parentElement : null;
  while (el) {
    const style = window.getComputedStyle(el);
    const canScrollY = /(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight;
    if (canScrollY) result.push(el);
    el = el.parentElement;
  }
  const root = document.scrollingElement || document.documentElement;
  if (!result.includes(root)) result.push(root);
  return result;
}

// ── Pagination ────────────────────────────────────────────────────────────────
// NOTE: now returns `perPage` too, so callers can pad short pages with filler
// rows and keep the table's height (and therefore scroll position) constant
// when navigating between pages.
function usePagination(rows, perPage = 5, anchorRef = null) {
  const [page, setPageRaw] = useState(1);
  const totalPages = Math.ceil(rows.length / perPage) || 1;
  useEffect(() => { if (page > totalPages) setPageRaw(totalPages); }, [rows.length]);

  // Scroll-lock: the instant a page change is requested we snapshot the
  // scrollTop of every scrollable ancestor of the table (not just window),
  // then force each one back to that value right after React re-renders —
  // and again on the next couple of animation frames, plus one final delayed
  // pass. Mobile browsers (iOS Safari/Chrome in particular) can re-scroll
  // asynchronously — e.g. to keep a newly-disabled button "in view", or as
  // part of a late focus-correction reflow — well after two RAFs have
  // already fired, so a single setTimeout catch-all closes that gap. The
  // snapshot is captured into a local `snapshot` array (not just left on the
  // ref) so the delayed restore still has data even after we clear the ref.
  const lockedPositions = useRef(null);

  useLayoutEffect(() => {
    if (!lockedPositions.current) return;
    const snapshot = lockedPositions.current;
    const restoreSnapshot = () => {
      snapshot.forEach(({ el, top }) => { el.scrollTop = top; });
    };

    restoreSnapshot();
    const frame1 = requestAnimationFrame(() => {
      restoreSnapshot();
      requestAnimationFrame(() => {
        restoreSnapshot();
        // Final catch-all: mobile browsers sometimes re-scroll after both
        // RAFs have settled (late focus-visible correction, async reflow).
        setTimeout(restoreSnapshot, 150);
      });
    });

    lockedPositions.current = null;
    return () => cancelAnimationFrame(frame1);
  }, [page]);

  const setPage = (updater) => {
    // Blur first so the browser never schedules a focus-follow scroll in
    // the first place, then snapshot scroll position for every ancestor.
    blurActiveElement();
    const ancestors = getScrollAncestors(anchorRef && anchorRef.current);
    lockedPositions.current = ancestors.map(el => ({ el, top: el.scrollTop }));
    setPageRaw(updater);
  };

  const currentRows = rows.slice((page - 1) * perPage, page * perPage);
  const buttons = () => {
    const start = Math.max(page - 1, 1);
    const end = Math.min(start + 2, totalPages);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(n => (
      <button key={n}
        onMouseDown={(e) => { e.preventDefault(); blurActiveElement(); }}
        onTouchStart={blurActiveElement}
        onClick={() => setPage(n)}
        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${n === page ? "text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
        style={n === page ? { background: "linear-gradient(135deg, #2563eb, #d12465)" } : undefined}>
        {n}
      </button>
    ));
  };
  return { page, setPage, totalPages, currentRows, buttons, perPage };
}

// Renders blank filler rows so a short last page doesn't shrink the table's
// height compared to a full page. This, combined with `overflow-anchor: none`
// on SectionCard, is what stops the page from "jumping"/scrolling when you
// click a pagination control.
function FillerRows({ count, colSpan }) {
  if (count <= 0) return null;
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <tr key={`filler-${i}`} aria-hidden="true">
          <td colSpan={colSpan} className="py-3 px-4">&nbsp;</td>
        </tr>
      ))}
    </>
  );
}



// Pagination nav buttons factored out so the blur/scroll-lock guard stays
// consistent everywhere. `onMouseDown` prevents the browser's default
// focus-follow behavior on click, and `onTouchStart` covers mobile taps
// (which don't reliably fire mousedown before click). Both blur proactively,
// before `setPage` runs, so no focus-follow scroll ever gets scheduled.
function PaginationNav({ page, setPage, totalPages, buttons }) {
  const guard = (e) => { e.preventDefault(); blurActiveElement(); };
  const go = (fn) => () => fn();
  return (
    <div className="flex items-center space-x-1">
      <button onMouseDown={guard} onTouchStart={blurActiveElement} onClick={go(() => setPage(1))} disabled={page === 1} className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40"><ChevronsLeft size={16}/></button>
      <button onMouseDown={guard} onTouchStart={blurActiveElement} onClick={go(() => setPage(p => Math.max(p - 1, 1)))} disabled={page === 1} className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40"><MdKeyboardArrowLeft size={16}/></button>
      {buttons()}
      <button onMouseDown={guard} onTouchStart={blurActiveElement} onClick={go(() => setPage(p => Math.min(p + 1, totalPages)))} disabled={page === totalPages} className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40"><MdKeyboardArrowRight size={16}/></button>
      <button onMouseDown={guard} onTouchStart={blurActiveElement} onClick={go(() => setPage(totalPages))} disabled={page === totalPages} className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40"><ChevronsRight size={16}/></button>
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl relative max-h-[88vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
          <h3 className="text-base font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
// The table itself now scrolls in its own fixed-height inner box (both
// vertically and horizontally), while `footer` — the pagination bar — is
// rendered below that box, outside the scroll area, so it stays put and
// never moves as the table content scrolls or changes page. This also means
// paging no longer needs to touch the outer page's scroll position at all:
// only the inner box scrolls, so there's nothing for mobile browsers to
// "correct" by jumping the page.
// overflowAnchor: "none" on the inner scroll box stops the browser's scroll-
// anchoring behavior from shifting its scroll position when content height
// changes (e.g. a shorter last page of results). forwardRef lets callers
// point usePagination's scroll-lock at this exact scrollable node.
const SectionCard = React.forwardRef(function SectionCard({ children, footer }, ref) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden flex flex-col">
      <div
        ref={ref}
        className="overflow-auto p-4 sm:p-6"
        style={{ overflowAnchor: "none", maxHeight: "60vh" }}
      >
        {children}
      </div>
      {footer && (
        <div className="shrink-0 border-t border-gray-100 bg-white px-4 sm:px-6 py-3">
          {footer}
        </div>
      )}
    </div>
  );
});

// ── Action buttons ────────────────────────────────────────────────────────────
function ApproveRejectButtons({ onApprove, onReject, status, role }) {
  if (status === "approved")
    return (
      <span className="text-green-600 text-xs font-semibold flex items-center gap-1">
        <Check size={13} /> Approved
      </span>
    );

  // Manager Approved
  if (status === "manager_approved") {
    // Manager login
    if (role === "manager") {
      return (
        <span className="text-blue-600 text-xs font-semibold flex items-center gap-1">
          <Check size={13} /> Manager Approved
        </span>
      );
    }

    // HR/Admin login -> still allow final approval
    return (
      <div className="flex gap-2">
        <button
          onClick={onApprove}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition"
        >
          <CheckCircle size={13} /> Approve
        </button>

        <button
          onClick={onReject}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition"
        >
          <XCircle size={13} /> Reject
        </button>
      </div>
    );
  }

  if (status === "rejected")
    return (
      <span className="text-red-500 text-xs font-semibold flex items-center gap-1">
        <XCircle size={13} /> Rejected
      </span>
    );

  return (
    <div className="flex gap-2">
      <button
        onClick={onApprove}
        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition"
      >
        <CheckCircle size={13} />
        {role === "manager" ? "Manager Approve" : "Approve"}
      </button>

      <button
        onClick={onReject}
        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition"
      >
        <XCircle size={13} /> Reject
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── Leave Tab ─────────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
function LeaveTab() {
  const {leaves, loading, updateLeaveStatus, managerApproval} = useLeaves();
  const {leaveTypes, loading: leaveTypeLoading} = useLeaveTypes();

  const [filterStatus, setFilterStatus] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [noteText, setNoteText] = useState("");

  const leaveData = leaves.map((item) => ({
    id: item.id,
    employee: `${item.first_name} ${item.last_name}`,
    department: "-", // API doesn't return department
    leaveType: item.leave_type?.toLowerCase(),
    from: item.start_date,
    to: item.end_date,
    days: Number(item.number_of_days),
    reason: item.description,
    status: item.status,
    appliedOn: item.create_audit_time?.split(" ")[0],
    managerNote: "",
  }));

const filtered = leaveData.filter(
  (l) =>
    (filterStatus === "All" || l.status === filterStatus) &&
    (filterType === "All" || l.leaveType === filterType) &&
    (
      l.employee.toLowerCase().includes(search.toLowerCase()) ||
      l.leaveType.toLowerCase().includes(search.toLowerCase())
    )
);


  const tableRef = useRef(null);
  const { page, setPage, totalPages, currentRows, buttons, perPage } = usePagination(filtered, 5, tableRef);
  const LEAVE_COLS = 7;

const counts = {
  pending: 0,
  manager_approved: 0,
  approved: 0,
  rejected: 0,
};

leaveData.forEach((l) => {
  if (counts.hasOwnProperty(l.status)) {
    counts[l.status]++;
  }
});

const loginUser = JSON.parse(localStorage.getItem("user"));
  const role = loginUser?.role?.toLowerCase();

const action = async (id, newStatus) => {
  if (!role) {
    alert("You don't have permission to approve/reject leave requests.");
    return;
  }

  try {
    let res;

    if (role === "manager") {
      // Manager's action is an intermediate approval step.
      res = await managerApproval(
        id,
        newStatus === "rejected" ? "rejected" : "manager_approved",
        noteText
      );
    } else if (role === "admin" || role === "hr") {
      // HR/Admin gives final approval.
      res = await updateLeaveStatus(
        id,
        newStatus === "rejected" ? "rejected" : "approved",
        noteText
      );
    } else {
      alert("You don't have permission to approve/reject leave requests.");
      return;
    }

    if (!res) {
      console.error("action(): API call returned no response", { id, newStatus, role });
      alert("Something went wrong — no response from server.");
      return;
    }

    if (res.success) {
      setModal(null);
      setNoteText("");
    } else {
      alert(res.message || "Failed to update leave status.");
    }
  } catch (err) {
    console.error("action() failed:", err);
    alert(err?.response?.data?.message || err?.message || "An unexpected error occurred.");
  }
};

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        {LEAVE_STATUS_OPTIONS.map(s => (
          <div key={s} onClick={() => setFilterStatus(filterStatus === s ? "All" : s)}
            className={`cursor-pointer p-3.5 rounded-2xl border bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col items-center gap-1 ${filterStatus === s ? "ring-2 ring-blue-500 border-transparent" : "border-gray-100"}`}>
            <div className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[s]}`} />
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{STATUS_LABEL[s]}</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{counts[s]}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white rounded-2xl border border-gray-100 px-4 py-3 shadow-md">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employee or leave type"
          className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none flex-1 min-w-[200px] focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition" />
        <select
  value={filterType}
  onChange={(e) => setFilterType(e.target.value)}
  className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
>
  <option value="All">All Types</option>

  {leaveTypes
    .filter((item) => item.status === "active")
    .map((item) => (
      <option
        key={item.id}
        value={item.name.toLowerCase()}
      >
        {item.name}
      </option>
    ))}
</select>
        {(search || filterStatus !== "All" || filterType !== "All") && (
          <button onClick={() => { setSearch(""); setFilterStatus("All"); setFilterType("All"); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition">
            <X size={14} /> Clear
          </button>
        )}
      </div>

      <SectionCard
        ref={tableRef}
        footer={
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">{filtered.length} request{filtered.length !== 1 ? "s" : ""}</span>
            <PaginationNav page={page} setPage={setPage} totalPages={totalPages} buttons={buttons} />
          </div>
        }
      >
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-sky-50">
              {["Employee","Leave Type","Duration","Days","Reason","Status","Actions"].map(h => (
                <th key={h} className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? (
              <>
                {currentRows.map(l => (
                  <tr key={l.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4"><div className="flex items-center gap-2"><Avatar name={l.employee} /><span className="text-gray-800">{l.employee}</span></div></td>
                    {/* <td className="py-3 px-4 text-gray-600">{l.department}</td> */}
                    <td className="py-3 px-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${LEAVE_TYPE_CLASS[l.leaveType] || "bg-gray-100 text-gray-600"}`}>{l.leaveType}</span>
                    </td>
                    <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{l.from} → {l.to}</td>
                    <td className="py-3 px-4 text-gray-800 text-center">{l.days}</td>
                    <td className="py-3 px-4 text-gray-600 max-w-[180px]">
                      <span className="line-clamp-2">{l.reason}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full ${STATUS_CLASS[l.status]}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[l.status]}`} />
                        {STATUS_LABEL[l.status]}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setModal({ type: "view", data: l });
                          }}
                          className="text-gray-400 hover:text-indigo-600 transition"
                        >
                          <Eye size={15} />
                        </button>

                        <ApproveRejectButtons
                          status={l.status}
                          role={role}
                          onApprove={() => {
                            setModal({ type: "approve", data: l });
                            setNoteText("");
                          }}
                          onReject={() => {
                            setModal({ type: "reject", data: l });
                            setNoteText("");
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                <FillerRows count={perPage - currentRows.length} colSpan={LEAVE_COLS} />
              </>
            ) : (
              <tr><td colSpan={LEAVE_COLS} className="text-center py-10 text-gray-400 text-sm">No leave requests found.</td></tr>
            )}
          </tbody>
        </table>
      </SectionCard>

      {/* View Modal */}
      <Modal open={modal?.type === "view"} onClose={() => setModal(null)} title="Leave Request Details">
        {modal?.data && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3 mb-4">
              <Avatar name={modal.data.employee} size="lg" />
              <div><div className="font-semibold text-gray-800 text-base">{modal.data.employee}</div><div className="text-gray-500">{modal.data.department}</div></div>
            </div>
            {[["Leave Type", modal.data.leaveType], ["From", modal.data.from], ["To", modal.data.to], ["Days", modal.data.days], ["Applied On", modal.data.appliedOn], ["Status", STATUS_LABEL[modal.data.status]]].map(([k,v]) => (
              <div key={k} className="flex justify-between border-b border-gray-50 pb-2">
                <span className="text-gray-500">{k}</span>
                <span className="font-medium text-gray-800 capitalize">{v}</span>
              </div>
            ))}
            <div><span className="text-gray-500 block mb-1">Reason</span><p className="text-gray-700 bg-gray-50 rounded-lg p-3">{modal.data.reason}</p></div>
            {modal.data.managerNote && <div><span className="text-gray-500 block mb-1">Manager Note</span><p className="text-gray-700 bg-green-50 rounded-lg p-3">{modal.data.managerNote}</p></div>}
          </div>
        )}
      </Modal>

      {/* Approve Modal */}
      <Modal open={modal?.type === "approve"} onClose={() => setModal(null)} title="Approve Leave Request">
        {modal?.data && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Approving <span className="font-semibold">{modal.data.employee}</span>'s {modal.data.leaveType} leave ({modal.data.days} day{modal.data.days>1?"s":""}).</p>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Note (optional)</label>
              <textarea rows="3" value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Add a note for the employee…"
                className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition" />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setModal(null)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => action(modal.data.id, "approved")} className="px-5 py-2 rounded-xl text-sm font-medium text-white bg-green-600 hover:bg-green-700 flex items-center gap-2"><CheckCircle size={15} /> Approve</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal open={modal?.type === "reject"} onClose={() => setModal(null)} title="Reject Leave Request">
        {modal?.data && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Rejecting <span className="font-semibold">{modal.data.employee}</span>'s {modal.data.leaveType} leave request.</p>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Reason for rejection <span className="text-red-500">*</span></label>
              <textarea rows="3" value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Provide a reason for rejection…"
                className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition" />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setModal(null)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => action(modal.data.id, "rejected")} disabled={!noteText.trim()} className="px-5 py-2 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"><XCircle size={15} /> Reject</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── Attendance Tab ────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
function AttendanceTab() {
  const {
  attendanceData,
  loading,
} = useAttendance();
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [noteText, setNoteText] = useState("");

   // ✅ FIX: define missing variables
  const now = new Date();

const today = `${String(now.getDate()).padStart(2, "0")}-${String(
  now.getMonth() + 1
).padStart(2, "0")}-${now.getFullYear()}`;

const currentHour = now.getHours();
  // ✅ FIX: build records from attendance_times
  const records = attendanceData.flatMap((item) => {
  const att = item.attendance_times?.[today];

  let status = "--";
  let checkIn = "--";
  let checkOut = "--";
  let type = "--";

  if (att) {
    status = att.status || "present";
    checkIn = att.check_in_time || "—";
    checkOut = att.check_out_time || "—";
    type = att.attendance_type || "face";
  } else {
    // ✅ only mark absent AFTER 4 PM
    status = currentHour >= 16 ? "absent" : "--";
  }

  return {
    id: `${item.user_id}-${today}`,
    employee: item.name,
    department: item.department_name || "-",
    date: today,
    checkIn,
    checkOut,
    hours: "0h",
    type,
    reason: "-",
    status,
  };
});
  const TYPES = ["face", "manual"];

const TYPE_LABEL = {
  face: "Face",
  manual: "Manual",
};

const TYPE_CLASS = {
  face: "bg-green-50 text-green-600 border border-green-200",
  manual: "bg-blue-50 text-blue-600 border border-blue-200",
};

  const filtered = records.filter(r =>
    (filterStatus === "All" || r.status === filterStatus) &&
    (filterType === "All" || r.type === filterType) &&
    (r.employee.toLowerCase().includes(search.toLowerCase()) || r.department.toLowerCase().includes(search.toLowerCase()))
  );

  const tableRef = useRef(null);
  const { page, setPage, totalPages, currentRows, buttons, perPage } = usePagination(filtered, 5, tableRef);
  const ATTENDANCE_COLS = 6;
  
  const counts = {};

  ATTENDANCE_STATUS_OPTIONS.forEach((status) => {
    counts[status] = 0;
  });

  records.forEach((r) => {
    if (counts[r.status] !== undefined) {
      counts[r.status]++;
    }
  });

  const action = (id, newStatus) => {
    // setRecords(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
    setModal(null); setNoteText("");
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {ATTENDANCE_STATUS_OPTIONS.map(s => (
          <div key={s} onClick={() => setFilterStatus(filterStatus === s ? "All" : s)}
            className={`cursor-pointer p-3.5 rounded-2xl border bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col items-center gap-1 ${filterStatus === s ? "ring-2 ring-blue-500 border-transparent" : "border-gray-100"}`}>
            <div className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[s]}`} />
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{STATUS_LABEL[s]}</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{counts[s]}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 bg-white rounded-2xl border border-gray-100 px-4 py-3 shadow-md">
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search employee"
          className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none flex-1 min-w-[200px] focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition" />
        <select value={filterType} onChange={e=>setFilterType(e.target.value)}
          className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition">
          <option value="All">All Types</option>
          {TYPES.map(t => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
        </select>
        {(search || filterStatus !== "All" || filterType !== "All") && (
          <button onClick={() => { setSearch(""); setFilterStatus("All"); setFilterType("All"); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition">
            <X size={14} /> Clear
          </button>
        )}
      </div>

      <SectionCard
        ref={tableRef}
        footer={
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
            <PaginationNav page={page} setPage={setPage} totalPages={totalPages} buttons={buttons} />
          </div>
        }
      >
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-sky-50">
              {["Employee","Date","Check In","Check Out","Type","Status"].map(h => (
                <th key={h} className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? (
              <>
                {currentRows.map(r => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4"><div className="flex items-center gap-2"><Avatar name={r.employee} /><span className="text-gray-800">{r.employee}</span></div></td>
                    {/* <td className="py-3 px-4 text-gray-600">{r.department}</td> */}
                    <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{r.date}</td>
                    <td className="py-3 px-4 text-gray-700">{r.checkIn}</td>
                    <td className="py-3 px-4 text-gray-700">{r.checkOut}</td>
                    {/* <td className="py-3 px-4 font-semibold text-gray-800">{r.hours}</td> */}
                    <td className="py-3 px-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${TYPE_CLASS[r.type] || "bg-gray-100 text-gray-600"}`}>{TYPE_LABEL[r.type] || r.type}</span>
                    </td>
                    {/* <td className="py-3 px-4 text-gray-600 max-w-[160px]"><span className="line-clamp-2">{r.reason}</span></td> */}
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full ${STATUS_CLASS[r.status]}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[r.status]}`} />
                        {STATUS_LABEL[r.status] || '--'}
                      </span>
                    </td>
                    {/* <td className="py-3 px-4">
                      <ApproveRejectButtons status={r.status}
                        onApprove={() => { setModal({ type: "approve", data: r }); setNoteText(""); }}
                        onReject={() => { setModal({ type: "reject", data: r }); setNoteText(""); }} />
                    </td> */}
                  </tr>
                ))}
                <FillerRows count={perPage - currentRows.length} colSpan={ATTENDANCE_COLS} />
              </>
            ) : (
              <tr><td colSpan={ATTENDANCE_COLS} className="text-center py-10 text-gray-400 text-sm">No attendance records found.</td></tr>
            )}
          </tbody>
        </table>
      </SectionCard>

      <Modal open={modal?.type === "approve"} onClose={() => setModal(null)} title="Approve Attendance Exception">
        {modal?.data && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Approving <span className="font-semibold">{modal.data.employee}</span>'s attendance exception on {modal.data.date}.</p>
            <div><label className="text-xs font-semibold text-gray-600 mb-1.5 block">Note (optional)</label>
              <textarea rows="3" value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Add a note…"
                className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 transition" /></div>
            <div className="flex gap-3 justify-end">
              <button onClick={()=>setModal(null)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={()=>action(modal.data.id,"approved")} className="px-5 py-2 rounded-xl text-sm font-medium text-white bg-green-600 hover:bg-green-700 flex items-center gap-2"><CheckCircle size={15}/>Approve</button>
            </div>
          </div>
        )}
      </Modal>
      <Modal open={modal?.type === "reject"} onClose={() => setModal(null)} title="Reject Attendance Exception">
        {modal?.data && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Rejecting <span className="font-semibold">{modal.data.employee}</span>'s attendance exception on {modal.data.date}.</p>
            <div><label className="text-xs font-semibold text-gray-600 mb-1.5 block">Reason <span className="text-red-500">*</span></label>
              <textarea rows="3" value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Reason for rejection…"
                className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 transition" /></div>
            <div className="flex gap-3 justify-end">
              <button onClick={()=>setModal(null)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={()=>action(modal.data.id,"rejected")} disabled={!noteText.trim()} className="px-5 py-2 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"><XCircle size={15}/>Reject</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── Team Tab ──────────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
function TeamTab() {
  const { teamData, loading, error } = useManagedashboard();
  const { user } = useAuth();
  const { employees: employeeDirectory } = useEmployee();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [modal, setModal] = useState(null);

  const TEAM_STATUSES = ["active","on_leave","remote"];

  const userRole = (user?.role || "").toLowerCase();
  const isManager = userRole === "manager";

  // The /team-management API returns every employee in the company, not just
  // the logged-in manager's direct reports. When viewed as a manager, narrow
  // it down to employees whose reporting_manager (from the employee
  // directory, matched by email since teamData has no employee id) is this
  // manager — same approach already used for the Work dashboard.
  const directReportEmails = useMemo(() => {
    if (!isManager) return null;
    return new Set(
      (employeeDirectory || [])
        .filter((emp) => emp.reporting_manager != null && String(emp.reporting_manager) === String(user?.id))
        .map((emp) => (emp.email || "").toLowerCase())
    );
  }, [employeeDirectory, isManager, user]);

   const team = useMemo(() => {
    const rows = (teamData || []).filter((m) =>
      !directReportEmails || directReportEmails.has((m.email || "").toLowerCase())
    );
    return rows.map((m, index) => ({
      id: index,
      name: m.employee_name,
      role: m.role,
      department: m.department,
      email: m.email,
      joinDate: m.joiningdate,
      status: m.status?.toLowerCase(),
      leaveBalance: m.leave_balance,
      attendance: parseFloat(m.attendance_percentage || 0),
      performanceScore: m.performance_score,
    }));
  }, [teamData, directReportEmails]);

  const filtered = team.filter(m =>
    (filterStatus === "All" || m.status === filterStatus) &&
    (
      m.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.role?.toLowerCase().includes(search.toLowerCase()) ||
      m.department?.toLowerCase().includes(search.toLowerCase())
    )
  );

  const tableRef = useRef(null);
  const { page, setPage, totalPages, currentRows, buttons, perPage } = usePagination(filtered, 5, tableRef);
  const TEAM_COLS = 10;

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        {TEAM_STATUSES.map(s => {
          const count = team.filter(m => m.status === s).length;
          return (
            <div key={s} onClick={() => setFilterStatus(filterStatus === s ? "All" : s)}
              className={`cursor-pointer p-3.5 rounded-2xl border bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col items-center gap-1 ${filterStatus === s ? "ring-2 ring-blue-500 border-transparent" : "border-gray-100"}`}>
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[s]}`} />
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{STATUS_LABEL[s]}</span>
              </div>
              <div className="text-2xl font-bold text-gray-800">{count}</div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3 bg-white rounded-2xl border border-gray-100 px-4 py-3 shadow-md">
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, role, or department…"
          className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none flex-1 min-w-[200px] focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition" />
        {(search || filterStatus !== "All") && (
          <button onClick={()=>{setSearch("");setFilterStatus("All");}} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition">
            <X size={14}/> Clear
          </button>
        )}
      </div>

      <SectionCard
        ref={tableRef}
        footer={
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">{filtered.length} member{filtered.length !== 1 ? "s" : ""}</span>
            <PaginationNav page={page} setPage={setPage} totalPages={totalPages} buttons={buttons} />
          </div>
        }
      >
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-sky-50">
              {["Employee","Role","Department","Email","Joined","Status","Leave Balance","Attendance %","Perf. Score","Actions"].map(h => (
                <th key={h} className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? (
              <>
                {currentRows.map(m => (
                  <tr key={m.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4"><div className="flex items-center gap-2"><Avatar name={m.name}/><span className="text-gray-800">{m.name}</span></div></td>
                    <td className="py-3 px-4 text-gray-600">{m.role}</td>
                    <td className="py-3 px-4 text-gray-600">{m.department}</td>
                    <td className="py-3 px-4 text-gray-600 text-xs">{m.email}</td>
                    <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{m.joinDate}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full ${STATUS_CLASS[m.status]}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[m.status]}`}/>
                        {STATUS_LABEL[m.status]}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`${m.leaveBalance <= 3 ? "text-red-600" : "text-gray-800"}`}>{m.leaveBalance}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{width:`${m.attendance}%`, background: m.attendance>=95?"#22c55e":m.attendance>=85?"#f59e0b":"#ef4444"}}/>
                        </div>
                        <span className="text-xs text-gray-700">{m.attendance}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-4"><StarRating score={m.performanceScore}/></td>
                    <td className="py-3 px-4">
                      <button onClick={()=>setModal({data:m})} className="text-gray-400 hover:text-indigo-600 transition"><Eye size={15}/></button>
                    </td>
                  </tr>
                ))}
                <FillerRows count={perPage - currentRows.length} colSpan={TEAM_COLS} />
              </>
            ) : (
              <tr><td colSpan={TEAM_COLS} className="text-center py-10 text-gray-400 text-sm">No team members found.</td></tr>
            )}
          </tbody>
        </table>
      </SectionCard>

      <Modal open={!!modal} onClose={() => setModal(null)} title="Team Member Details">
        {modal?.data && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3 mb-4">
              <Avatar name={modal.data.name} size="lg"/>
              <div><div className="font-semibold text-gray-800 text-base">{modal.data.name}</div><div className="text-gray-500">{modal.data.role} · {modal.data.department}</div></div>
            </div>
            {[["Email", modal.data.email],["Joined", modal.data.joinDate],["Status", STATUS_LABEL[modal.data.status]],["Leave Balance", `${modal.data.leaveBalance} days`],["Attendance", `${modal.data.attendance}%`]].map(([k,v]) => (
              <div key={k} className="flex justify-between border-b border-gray-50 pb-2">
                <span className="text-gray-500">{k}</span><span className="font-medium text-gray-800">{v}</span>
              </div>
            ))}
            <div className="flex justify-between pb-2">
              <span className="text-gray-500">Performance Score</span>
              <StarRating score={modal.data.performanceScore}/>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── Performance Tab ───────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
//
// CHANGE LOG (this revision):
// - Removed the entire localStorage-based 15-day cooldown mechanism
//   (getLocalReviewTimestamp / setLocalReviewTimestamp / REVIEW_COOLDOWN_DAYS /
//   daysSince / cooldownActive / daysRemaining). It was causing a desync: the
//   frontend would hide the Review button because a stale/incorrect local
//   timestamp said "recently reviewed", even when the backend had no record of
//   that review at all (confirmed via the API response for "hello test" —
//   their latest backend row is Q1 2026 "completed", with nothing for the
//   current Q3 2026 period, yet the button was hidden with "Available in 15d").
// - The Review button's visibility is now based ONLY on backend truth:
//     r.status !== "completed" && r.reviewPeriod === CURRENT_PERIOD
//   i.e. visible any time the current period's review isn't completed yet.
// - If/when the backend adds a real submitted/updated timestamp field
//   (e.g. reviewed_at), a cooldown can be reintroduced safely by deriving
//   elapsed time from that field only — do not reintroduce any localStorage
//   fallback, since it can't be trusted across devices/users and was the
//   root cause of this bug.

// The API stores review_period as "Q<n> <year>" (e.g. "Q3 2026"), so "current
// period" must be computed the same way or it will never match a real row —
// that mismatch was the root cause of the button/feedback bug.
function getCurrentQuarterPeriod() {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3) + 1;
  return `Q${quarter} ${now.getFullYear()}`;
}

// Maps a "Q<n> <year>" period into { year, quarter } for chronological
// sorting and month-range display. Returns null if it doesn't match.
const QUARTER_MONTH_RANGE = { 1: "Jan–Mar", 2: "Apr–Jun", 3: "Jul–Sep", 4: "Oct–Dec" };
function parseQuarterPeriod(period) {
  const match = /^Q([1-4])\s+(\d{4})$/i.exec((period || "").trim());
  if (!match) return null;
  return { quarter: Number(match[1]), year: Number(match[2]) };
}

// Display label for a review period. For the "Q<n> <year>" shape the API
// actually returns, this appends the quarter's month range (e.g.
// "Q3 2026 (Jul–Sep)"). Falls back to the raw string for any other shape.
function formatReviewPeriod(period) {
  if (!period || period === "-") return period;
  const parsed = parseQuarterPeriod(period);
  if (!parsed) return period;
  return `${period} (${QUARTER_MONTH_RANGE[parsed.quarter]})`;
}

// Sorts period strings most-recent-first by actual year/quarter rather than
// alphabetically, so e.g. "Q1 2027" correctly sorts ahead of "Q4 2026".
function compareReviewPeriodsDesc(a, b) {
  const pa = parseQuarterPeriod(a);
  const pb = parseQuarterPeriod(b);
  if (pa && pb) {
    if (pa.year !== pb.year) return pb.year - pa.year;
    return pb.quarter - pa.quarter;
  }
  return String(b).localeCompare(String(a));
}

function PerformanceTab() {
  const { reviews, fetchReviews, submitReview: submitReviewAPI, loading } = usePerformance();
  const { user: loginUser } = useAuth();
  const CURRENT_PERIOD = useMemo(() => getCurrentQuarterPeriod(), []);

  const [filterStatus, setFilterStatus] = useState("All");
  // Default to the current quarter so managers land on "this quarter's"
  // reviews; they can still switch to any earlier period to browse history.
  const [filterPeriod, setFilterPeriod] = useState(getCurrentQuarterPeriod());
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [scoreInput, setScoreInput] = useState("");
  const [goalsTotalInput, setGoalsTotalInput] = useState("");
  const [goalsAchievedInput, setGoalsAchievedInput] = useState("");
  const [feedbackInput, setFeedbackInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const team = useMemo(() => {
    const mapped = (reviews || []).map((r, index) => {
      const rawScore = r.score;
      const hasScore = rawScore !== null && rawScore !== undefined && rawScore !== "";
      const parsedScore = hasScore ? Number(rawScore) : null;
      const score = parsedScore !== null && !Number.isNaN(parsedScore) ? parsedScore : null;

      const goalsCompletedRaw =
        r.goalsCompletedFromTasks ??
        r.goals_completed ??
        r.completedGoals ??
        r.goalsCompleted ??
        0;

      // Goals total wasn't rendering — the API field name apparently doesn't
      // match `r.goals` for every payload shape. Fall back across the
      // likely alternates until one resolves, same pattern as goalsCompleted.
      const goalsRaw =
        r.goals ??
        r.total_goals ??
        r.totalGoals ??
        r.goalsCount ??
        r.goals_count ??
        0;

      // Same issue for feedback — try the common alternate field names
      // before giving up and showing "no feedback yet".
      const feedbackRaw =
        r.feedback ??
        r.review_feedback ??
        r.reviewFeedback ??
        r.comments ??
        r.manager_feedback ??
        r.managerFeedback ??
        "";

      return {
        // id includes the period so two periods for the same employee never
        // collide/overwrite each other in the table.
        id: `${r.user_id ?? r.employee ?? index}-${r.reviewPeriod ?? "unknown"}`,
        userId: r.user_id ?? null, // kept separately — this is what submitReviewAPI needs
        employee: r.employee ?? "Unknown",
        department: r.department ?? "-",
        reviewPeriod: r.reviewPeriod ?? "-",
        goals: Number(goalsRaw) || 0,
        goalsCompleted: Number(goalsCompletedRaw) || 0,
        score,
        status: r.status ?? "not_started",
        dueDate: r.dueDate ?? "-",
        feedback: feedbackRaw,
      };
    });

    // Ensure every employee has a row for the CURRENT period. If the API
    // hasn't created one yet (e.g. a brand-new quarter just started), synthesize
    // a "not_started" placeholder so the Review button shows up again without
    // waiting on a backend job to create the row first.
    const byEmployee = new Map();
    mapped.forEach((r) => {
      if (!byEmployee.has(r.employee)) byEmployee.set(r.employee, []);
      byEmployee.get(r.employee).push(r);
    });

    byEmployee.forEach((rows, employee) => {
      const hasCurrent = rows.some((r) => r.reviewPeriod === CURRENT_PERIOD);
      if (!hasCurrent) {
        // Borrow department/userId from the most recent known row for this employee.
        const latest = rows[0];
        mapped.push({
          id: `${latest?.userId ?? employee}-${CURRENT_PERIOD}`,
          userId: latest?.userId ?? null,
          employee,
          department: latest?.department ?? "-",
          reviewPeriod: CURRENT_PERIOD,
          goals: 0,
          goalsCompleted: 0,
          score: null,
          status: "not_started",
          dueDate: "-",
          feedback: "",
        });
      }
    });

    return mapped;
  }, [reviews, CURRENT_PERIOD]);

  // unique list of review periods, most recent first
  const availablePeriods = useMemo(() => {
    const periods = Array.from(new Set(team.map((r) => r.reviewPeriod).filter((p) => p && p !== "-")));
    return periods.sort(compareReviewPeriodsDesc);
  }, [team]);

  const searchText = search.toLowerCase();

  const filtered = team.filter((r) => {
    const employee = (r.employee ?? "").toLowerCase();
    const department = (r.department ?? "").toLowerCase();

    return (
      (filterStatus === "All" || r.status === filterStatus) &&
      (filterPeriod === "All" || r.reviewPeriod === filterPeriod) &&
      (employee.includes(searchText) || department.includes(searchText))
    );
  });

  const tableRef = useRef(null);
  const { page, setPage, totalPages, currentRows, buttons, perPage } = usePagination(filtered, 5, tableRef);
  const PERFORMANCE_COLS = 8;

  // Summary cards reflect the CURRENT period's status breakdown, so they
  // naturally reset each quarter along with the Review button.
  const counts = {};
  PERFORMANCE_STATUS_OPTIONS.forEach((s) => {
    counts[s] = 0;
  });
  team
    .filter((r) => r.reviewPeriod === CURRENT_PERIOD)
    .forEach((r) => {
      if (counts[r.status] !== undefined) counts[r.status]++;
    });

  const STATUS_PERF_LABEL = { not_started: "Not Started", in_progress: "In Progress", completed: "Completed" };

  const submitReview = async (id) => {
    const goalsTotal = parseInt(goalsTotalInput, 10);
    const goalsAchieved = parseInt(goalsAchievedInput, 10);

    if (Number.isNaN(goalsTotal) || Number.isNaN(goalsAchieved)) {
      alert("Please enter both total goals and goals achieved.");
      return;
    }
    if (goalsAchieved > goalsTotal) {
      alert("Goals achieved can't be more than total goals.");
      return;
    }

    setSubmitting(true);

    try {
      const row = team.find((r) => r.id === id);
      if (!row?.userId) {
        alert("Missing employee id — can't submit this review.");
        return;
      }

      const res = await submitReviewAPI({
        user_id: row.userId,
        review_period: row.reviewPeriod,
        score: parseFloat(scoreInput),
        // Best-guess field names for "goals reached" — confirm these match
        // your backend's submitReview endpoint and rename if needed.
        goals: goalsTotal,
        goals_completed: goalsAchieved,
        feedback: feedbackInput,
        user_session_id: loginUser?.id,
      });

      if (res.success) {
        setModal(null);
        setScoreInput("");
        setGoalsTotalInput("");
        setGoalsAchievedInput("");
        setFeedbackInput("");
        fetchReviews(); // refresh so the synthesized row is replaced by the real one
      } else if (res.alreadyExists) {
        alert("Already submitted for this period");
      } else {
        alert(res.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {PERFORMANCE_STATUS_OPTIONS.map((s) => (
          <div
            key={s}
            onClick={() => setFilterStatus(filterStatus === s ? "All" : s)}
            className={`cursor-pointer p-3.5 rounded-2xl border bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col items-center gap-1 ${
              filterStatus === s ? "ring-2 ring-blue-500 border-transparent" : "border-gray-100"
            }`}
          >
            <div className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[s]}`} />
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{STATUS_PERF_LABEL[s]}</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{counts[s]}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 bg-white rounded-2xl border border-gray-100 px-4 py-3 shadow-md">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search employee or department…"
          className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none flex-1 min-w-[200px] focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
        />

        {/* Period filter — options are labelled with their quarter's month
            range (e.g. "Q3 2026 (Jul–Sep)") so managers can spot the review
            period they're looking for at a glance. */}
        <select
          value={filterPeriod}
          onChange={(e) => setFilterPeriod(e.target.value)}
          className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
        >
          <option value="All">All Periods</option>
          {availablePeriods.map((p) => (
            <option key={p} value={p}>
              {formatReviewPeriod(p)} {p === CURRENT_PERIOD ? "(current)" : ""}
            </option>
          ))}
        </select>

        {(search || filterStatus !== "All" || filterPeriod !== CURRENT_PERIOD) && (
          <button
            onClick={() => {
              setSearch("");
              setFilterStatus("All");
              setFilterPeriod(CURRENT_PERIOD);
            }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition"
          >
            <X size={14} /> Clear
          </button>
        )}
      </div>

      <SectionCard
        ref={tableRef}
        footer={
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">
              {filtered.length} review{filtered.length !== 1 ? "s" : ""}
            </span>
            <PaginationNav page={page} setPage={setPage} totalPages={totalPages} buttons={buttons} />
          </div>
        }
      >
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="bg-sky-50">
              {["Employee", "Department", "Review Period", "Goals", "Score", "Status", "Due Date", "Actions"].map((h) => (
                <th key={h} className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? (
              <>
                {currentRows.map((r) => {
                  // Review is only actionable for the CURRENT period's row and
                  // only while it isn't completed yet — gated purely on
                  // backend-provided status, no cooldown, no local state.
                  const canReview = r.status !== "completed" && r.reviewPeriod === CURRENT_PERIOD;

                  return (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Avatar name={r.employee} />
                        <span className="text-gray-800">{r.employee}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{r.department}</td>
                    <td className="py-3 px-4 text-gray-600">
                      {formatReviewPeriod(r.reviewPeriod)}
                      {r.reviewPeriod === CURRENT_PERIOD && (
                        <span className="ml-1.5 text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full">
                          Current
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-14 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-indigo-500 rounded-full"
                            style={{ width: r.goals > 0 ? `${(r.goalsCompleted / r.goals) * 100}%` : "0%" }}
                          />
                        </div>
                        <span className="text-xs text-gray-600">
                          {r.goalsCompleted}/{r.goals}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <StarRating score={r.score} />
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full ${STATUS_CLASS[r.status]}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[r.status]}`} />
                        {STATUS_PERF_LABEL[r.status]}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{r.dueDate}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setModal({ type: "view", data: r })} className="text-gray-400 hover:text-indigo-600 transition">
                          <Eye size={15} />
                        </button>
                        {canReview && (
                          <button
                            onClick={() => {
                              setModal({ type: "review", data: r });
                              setScoreInput("");
                              setGoalsTotalInput(r.goals ? String(r.goals) : "");
                              setGoalsAchievedInput(r.goalsCompleted ? String(r.goalsCompleted) : "");
                              setFeedbackInput("");
                            }}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition"
                          >
                            <BarChart2 size={12} /> Review
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
                <FillerRows count={perPage - currentRows.length} colSpan={PERFORMANCE_COLS} />
              </>
            ) : (
              <tr>
                <td colSpan={PERFORMANCE_COLS} className="text-center py-10 text-gray-400 text-sm">
                  No performance reviews found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </SectionCard>

      <Modal open={modal?.type === "view"} onClose={() => setModal(null)} title="Performance Review Details">
        {modal?.data && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3 mb-4">
              <Avatar name={modal.data.employee} size="lg" />
              <div>
                <div className="font-semibold text-gray-800 text-base">{modal.data.employee}</div>
                <div className="text-gray-500">
                  {modal.data.department} · {formatReviewPeriod(modal.data.reviewPeriod)}
                </div>
              </div>
            </div>
            {[
              ["Due Date", modal.data.dueDate],
              ["Goals", `${modal.data.goalsCompleted} of ${modal.data.goals} completed`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-gray-50 pb-2">
                <span className="text-gray-500">{k}</span>
                <span className="font-medium text-gray-800">{v}</span>
              </div>
            ))}
            <div className="flex justify-between border-b border-gray-50 pb-2">
              <span className="text-gray-500">Score</span>
              <StarRating score={modal.data.score} />
            </div>
            {modal.data.feedback && (
              <div>
                <span className="text-gray-500 block mb-1">Feedback</span>
                <p className="text-gray-700 bg-indigo-50 rounded-lg p-3 leading-relaxed">{modal.data.feedback}</p>
              </div>
            )}
            {!modal.data.feedback && <p className="text-gray-400 text-xs italic">No feedback submitted yet.</p>}
          </div>
        )}
      </Modal>

      <Modal open={modal?.type === "review"} onClose={() => setModal(null)} title="Submit Performance Review">
        {modal?.data && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Avatar name={modal.data.employee} size="lg" />
              <div>
                <div className="font-semibold text-gray-800">{modal.data.employee}</div>
                <div className="text-gray-500 text-xs">
                  {modal.data.department} · {formatReviewPeriod(modal.data.reviewPeriod)}
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                Score (1.0 – 5.0) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                max="5"
                step="0.1"
                value={scoreInput}
                onChange={(e) => setScoreInput(e.target.value)}
                placeholder="e.g. 4.2"
                className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                  Total Goals <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={goalsTotalInput}
                  onChange={(e) => setGoalsTotalInput(e.target.value)}
                  placeholder="e.g. 5"
                  className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                  Goals Achieved <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={goalsAchievedInput}
                  onChange={(e) => setGoalsAchievedInput(e.target.value)}
                  placeholder="e.g. 3"
                  className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                Feedback <span className="text-red-500">*</span>
              </label>
              <textarea
                rows="4"
                value={feedbackInput}
                onChange={(e) => setFeedbackInput(e.target.value)}
                placeholder="Provide detailed performance feedback…"
                className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 focus:bg-white transition"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setModal(null)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => submitReview(modal.data.id)}
                disabled={!scoreInput || !goalsTotalInput || !goalsAchievedInput || !feedbackInput.trim() || submitting}
                style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
                className="px-5 py-2 rounded-xl text-sm font-medium text-white disabled:opacity-50 flex items-center gap-2 hover:opacity-90"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle size={15} />
                    Submit Review
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
// ══════════════════════════════════════════════════════════════════════════════
// ── Main Component ────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
export default function ManagerDashboard() {
  const [activeTab, setActiveTab] = useState("leave");

  const TAB_CONTENT = {
    leave: <LeaveTab />,
    attendance: <AttendanceTab />,
    team: <TeamTab />,
    performance: <PerformanceTab />,
  };

  // Quick stats for header
  const pendingLeaves = MOCK_LEAVES.filter(l => l.status === "pending").length;
  const pendingAttendance = MOCK_ATTENDANCE.filter(a => a.status === "pending").length;
  const teamSize = MOCK_TEAM.length;
  const pendingReviews = MOCK_PERFORMANCE.filter(r => r.status !== "completed").length;

  const QUICK_STATS = [
    { label: "Pending Leaves", value: pendingLeaves, icon: CalendarCheck, color: "text-yellow-600", bg: "bg-yellow-50" },
    { label: "Attendance Exceptions", value: pendingAttendance, icon: Clock, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Team Members", value: teamSize, icon: Users, color: "text-indigo-600", bg: "bg-indigo-50" },
    { label: "Pending Reviews", value: pendingReviews, icon: TrendingUp, color: "text-pink-600", bg: "bg-pink-50" },
  ];

  return (
    <div className="bg-sky-50 min-h-screen">
      <div className="px-6 py-6 sm:px-9 max-sm:px-4">
        {/* Header */}
        <div className="mb-5">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Manager Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Approve requests, track your team, and manage performance all in one place.</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {QUICK_STATS.map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 hover:shadow-md transition">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bg} shrink-0`}>
                <Icon size={18} className={color} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-800">{value}</div>
                <div className="text-xs text-gray-500 leading-tight">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-md mb-5 overflow-hidden">
          <div className="flex overflow-x-auto border-b border-gray-100">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-medium whitespace-nowrap transition-all border-b-2 ${
                  activeTab === key
                    ? "border-blue-600 text-blue-600 bg-blue-50/50"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}>
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {TAB_CONTENT[activeTab]}
      </div>
    </div>
  );
}